// frontend/src/components/FeaturedSlider.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import featuredService from "../services/featuredService";
import { Loader2, X, ZoomIn } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function FeaturedSlider() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadFeaturedItems();
  }, []);

  const loadFeaturedItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await featuredService.getActive();
      if (result.success) {
        setItems(result.data);
      } else {
        setError(result.message || 'Erro ao carregar destaques');
      }
    } catch (error) {
      console.error('Error loading featured items:', error);
      setError('Erro ao carregar destaques');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    const lightboxUrl = item.imagem_lightbox || item.imagem;
    
    setLightboxImage({ 
      url: lightboxUrl, 
      titulo: item.titulo, 
      showTitle: item.show_title 
    });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  if (error || items.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-6 text-center border border-gray-200">
        <p className="text-gray-400 text-sm">Nenhum destaque disponível</p>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={16}
          slidesPerView={1.2}
          breakpoints={{
            480: { slidesPerView: 1.5 },
            640: { slidesPerView: 2.2 },
            768: { slidesPerView: 3.2 },
            1024: { slidesPerView: 4.2 },
            1280: { slidesPerView: 5.2 },
          }}
          navigation={{
            nextEl: '.featured-button-next',
            prevEl: '.featured-button-prev',
          }}
          pagination={{ 
            clickable: true, 
            dynamicBullets: true,
            renderBullet: (index, className) => 
              `<span class="${className} !bg-[#449879]/50 !w-2 !h-2"></span>`
          }}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop={items.length > 3}
          className="pb-12"
        >
          {items.map((item) => {
            const imageUrl = item.imagem || null;
            const hasImage = imageUrl && imageUrl !== 'null' && imageUrl !== '';
            
            const linkTo = item.link || (item.post_id ? `/produto/${item.post_id}` : '#');
            const gradientColor = item.cor || 'from-emerald-500 to-green-600';
            const showTitle = item.show_title !== undefined ? item.show_title : true;
            const showDescription = item.show_description !== undefined ? item.show_description : true;
            
            return (
              <SwiperSlide key={item.id}>
                <div className="relative group aspect-[16/9] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  
                  <div className="relative w-full h-full">
                    
                    {hasImage ? (
                      <div 
                        className="w-full h-full cursor-zoom-in"
                        onClick={(e) => openLightbox(e, item)}
                      >
                        <img
                          src={imageUrl}
                          alt={item.titulo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => {
                            console.error('❌ Error loading image:', imageUrl);
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                        
                        {showTitle && (
                          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                            <h3 className="text-white font-bold text-sm md:text-base line-clamp-2 drop-shadow-lg">
                              {item.titulo}
                            </h3>
                            {showDescription && item.descricao && (
                              <p className="text-white/80 text-xs line-clamp-1 mt-0.5">
                                {item.descricao}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* 🔥 MOBILE: Solo lupa, sin texto */}
                        {isMobile && (
                          <button
                            className="absolute bottom-3 right-3 bg-black/70 hover:bg-[#449879] text-white p-2.5 rounded-xl backdrop-blur-sm shadow-lg transition-all duration-300 hover:scale-105 opacity-100"
                            onClick={(e) => openLightbox(e, item)}
                            aria-label="Ver imagem ampliada"
                          >
                            <ZoomIn className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div 
                        className={`w-full h-full bg-gradient-to-r ${gradientColor} flex items-center justify-center p-6 cursor-default`}
                      >
                        <div className="text-center text-white max-w-[90%]">
                          {showTitle && (
                            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 drop-shadow-lg">
                              {item.titulo}
                            </h3>
                          )}
                          {showDescription && item.descricao && (
                            <p className="text-white/90 text-sm md:text-base drop-shadow-md">
                              {item.descricao}
                            </p>
                          )}
                          {item.link && (
                            <Link 
                              to={linkTo} 
                              className="inline-block mt-3 text-white/70 hover:text-white text-sm font-medium transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Saiba mais →
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="absolute top-3 right-3 flex gap-1.5 pointer-events-none">
                      {item.tipo === 'produto' && (
                        <span className="bg-[#449879] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-lg">
                          Produto
                        </span>
                      )}
                      {item.tipo === 'externo' && (
                        <span className="bg-[#449879] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-lg">
                          Publicidade
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <button className="featured-button-prev absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-8 h-8 lg:w-10 lg:h-10 bg-white/90 hover:bg-[#449879] text-gray-700 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm md:flex hidden">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        
        <button className="featured-button-next absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-8 h-8 lg:w-10 lg:h-10 bg-white/90 hover:bg-[#449879] text-gray-700 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm md:flex hidden">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* LIGHTBOX */}
      {lightboxOpen && lightboxImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center animate-fadeIn"
          onClick={closeLightbox}
        >
          <div className="relative w-full max-w-6xl max-h-[95vh] mx-4 md:mx-8">
            
            <div 
              className="relative bg-transparent rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-3 md:p-4 pointer-events-none">
                {lightboxImage.showTitle && lightboxImage.titulo && (
                  <span className="text-white text-xs md:text-sm font-medium truncate max-w-[200px] md:max-w-[300px] drop-shadow-lg pointer-events-auto">
                    {lightboxImage.titulo}
                  </span>
                )}
                
                <button
                  onClick={closeLightbox}
                  className="bg-black/50 hover:bg-red-500 text-white p-2 rounded-full transition-all duration-300 border border-white/30 hover:border-red-500 shadow-lg flex-shrink-0 ml-2 pointer-events-auto"
                  aria-label="Fechar"
                >
                  <X className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
              
              <div className="flex items-center justify-center p-2 md:p-4 pt-12 md:pt-14">
                <img
                  src={lightboxImage.url}
                  alt={lightboxImage.titulo}
                  className="max-w-full max-h-[75vh] md:max-h-[80vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <div className="mt-4 text-center">
              <span className="text-white/40 text-xs md:text-sm">
                {isMobile ? '👆 Toque fora da imagem para fechar' : 'Clique fora da imagem para fechar'}
              </span>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.25s ease-out;
          }
          .cursor-zoom-in {
            cursor: zoom-in;
          }
        `}
      </style>
    </>
  );
}